import { Navigate, Outlet } from 'react-router-dom';
import { ReactNode } from 'react';
import { usePermissions } from '@/context/PermissionContext';
import Loader from '@/components/ui/loader';

type AccessType =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'detailedView'
  | 'manageRequest'
  | 'manageAccount';

type ModuleRouteProps = {
  moduleName: string | string[];
  children?: ReactNode;
  requiredAccess?: AccessType | AccessType[];
  anyAccess?: AccessType | AccessType[];
};

const ROUTE_PRIORITY = [
  { permissionKey: 'Dashboard', href: '/dashboard' },
  { permissionKey: 'Users', href: '/members/4' },
  { permissionKey: 'BankingManagement', href: '/requests' },
  { permissionKey: 'RiskManagement', href: '/risk' },
  { permissionKey: 'MyReport', href: '/bet-list' },
  { permissionKey: 'EventManagement', href: '/event-management' },
  { permissionKey: 'CasinoControl', href: '/casino-management' },
  { permissionKey: 'Transaction', href: '/requests' },
  { permissionKey: 'ContentManagement', href: '/content-management' },
  { permissionKey: 'Bonus', href: '/bonus/policy-management' },
  { permissionKey: 'Roles Privileges', href: '/roles-and-privileges' },
  { permissionKey: 'Account Statement', href: '/account-statement' },
  { permissionKey: 'Commission Settings', href: '/commission-setting' },
  { permissionKey: 'Change Password', href: '/settings' },
  { permissionKey: 'Operation logs', href: '/operation-logs' },
];

const ModuleRoute = ({
  moduleName,
  children,
  requiredAccess = 'read',
  anyAccess,
}: ModuleRouteProps) => {
  const { hasModuleAccess, isOwner, isAgent, user } = usePermissions();

  if (!user && !isOwner && !isAgent) return <Loader />;

  const accessList = Array.isArray(requiredAccess) ? requiredAccess : [requiredAccess];
  const moduleList = Array.isArray(moduleName) ? moduleName : [moduleName];

  const anyAccessList = anyAccess ? (Array.isArray(anyAccess) ? anyAccess : [anyAccess]) : null;
  const hasRequiredAccess =
    isAgent ||
    isOwner ||
    moduleList.some((mod) => accessList.every((access) => hasModuleAccess(mod, access))) ||
    (anyAccessList !== null &&
      moduleList.some((mod) => anyAccessList.some((access) => hasModuleAccess(mod, access))));

  const primaryModule = moduleList[0];

  if (!hasRequiredAccess) {
    if (primaryModule === 'Dashboard') {
      const firstAccessibleRoute = ROUTE_PRIORITY.find(
        (route) => isOwner || isAgent || hasModuleAccess(route.permissionKey, 'read')
      );

      return <Navigate to={firstAccessibleRoute?.href || '/settings'} replace />;
    }

    return <Navigate to="/settings" replace />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
};

export default ModuleRoute;
